import React from "react";

/* ================================================================
   People & Characters — 8 animated templates
   Family, team, individuals, teacher, couple, crowd, handshake
   Mix of flat 2D and soft 3D styles
   ================================================================ */

// ── 1. Family Group ──────────────────────────────────────────────────
export const FamilyGroup: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const members = ["Dad", "Mom", "Child", "Baby"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <defs>
        <radialGradient id="fg-floor"><stop offset="0%" stopColor="#e0e7ff" /><stop offset="100%" stopColor="#c7d2fe" /></radialGradient>
      </defs>
      <ellipse cx="400" cy="420" rx="350" ry="40" fill="url(#fg-floor)" opacity="0.5" />
      {/* Dad */}
      <g opacity={activeIndex >= 0 ? 1 : 0.3}>
        <circle cx="220" cy="250" r="28" fill="#6366f1" /><circle cx="220" cy="222" r="3" fill="#fff" />
        <rect x="208" y="278" width="24" height="50" rx="8" fill="#4f46e5" />
        <rect x="204" y="328" width="14" height="40" rx="5" fill="#4338ca" />
        <rect x="222" y="328" width="14" height="40" rx="5" fill="#4338ca" />
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-3;0,0" dur="3s" repeatCount="indefinite" />
      </g>
      {/* Mom */}
      <g opacity={activeIndex >= 1 ? 1 : 0.3}>
        <circle cx="320" cy="255" r="26" fill="#ec4899" /><circle cx="320" cy="229" r="3" fill="#fff" />
        <path d="M300,283 Q310,330 320,340 Q330,330 340,283 Z" fill="#db2777" />
        <rect x="306" y="340" width="12" height="35" rx="5" fill="#be185d" />
        <rect x="322" y="340" width="12" height="35" rx="5" fill="#be185d" />
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-2;0,0" dur="2.5s" repeatCount="indefinite" />
      </g>
      {/* Child */}
      <g opacity={activeIndex >= 2 ? 1 : 0.3}>
        <circle cx="440" cy="300" r="20" fill="#f59e0b" /><circle cx="440" cy="280" r="2.5" fill="#fff" />
        <rect x="430" y="320" width="20" height="35" rx="6" fill="#d97706" />
        <rect x="428" y="355" width="10" height="25" rx="4" fill="#b45309" />
        <rect x="442" y="355" width="10" height="25" rx="4" fill="#b45309" />
        <animateTransform attributeName="transform" type="translate" values="0,0;2,-4;0,0" dur="1.8s" repeatCount="indefinite" />
      </g>
      {/* Baby */}
      <g opacity={activeIndex >= 3 ? 1 : 0.3}>
        <circle cx="540" cy="340" r="16" fill="#34d399" />
        <ellipse cx="540" cy="370" rx="14" ry="18" fill="#10b981" />
        <animateTransform attributeName="transform" type="translate" values="0,0;1,-2;-1,0;0,0" dur="2s" repeatCount="indefinite" />
      </g>
      {/* Heart connecting */}
      <path d="M280,240 Q300,200 320,240" stroke="#f472b6" strokeWidth="2" fill="none" opacity="0.6">
        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" />
      </path>
      {/* Labels */}
      {members.map((m, i) => (
        <text key={m} x={220 + i * 110} y={410} textAnchor="middle" fill={activeIndex === i ? "#e0e7ff" : "#64748b"} fontSize="13" fontWeight={activeIndex === i ? "bold" : "normal"}>{m}</text>
      ))}
      <text x="400" y="60" textAnchor="middle" fill="#e0e7ff" fontSize="20" fontWeight="bold">Family</text>
    </svg>
  );
};

// ── 2. Team Circle ───────────────────────────────────────────────────
export const TeamCircle: React.FC<Record<string, unknown>> = (props) => {
  const count = (props.count as number) ?? 6;
  const activeIndex = (props.activeIndex as number) ?? -1;
  const colors = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <circle cx="400" cy="250" r="120" fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="4,6" />
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
        const x = 400 + Math.cos(angle) * 120;
        const y = 250 + Math.sin(angle) * 120;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={isActive ? 28 : 22} fill={colors[i % colors.length]} opacity={isActive ? 1 : 0.7}>
              {isActive && <animate attributeName="r" values="22;28;22" dur="1s" repeatCount="indefinite" />}
            </circle>
            <circle cx={x} cy={y - 8} r="3" fill="#fff" opacity="0.8" />
            <line x1={x} y1={y} x2="400" y2="250" stroke={colors[i % colors.length]} strokeWidth="1" opacity="0.3" />
          </g>
        );
      })}
      {/* Center connection pulse */}
      <circle cx="400" cy="250" r="15" fill="#6366f1" opacity="0.3">
        <animate attributeName="r" values="10;20;10" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="440" textAnchor="middle" fill="#e0e7ff" fontSize="18" fontWeight="bold">Team Collaboration</text>
    </svg>
  );
};

// ── 3. Single Girl ───────────────────────────────────────────────────
export const SingleGirl: React.FC<Record<string, unknown>> = (props) => {
  const emotion = (props.emotion as string) ?? "neutral";
  const phase = (props.phase as number) ?? 0;
  const breathe = Math.sin(phase * 0.5) * 2;
  const smileCurve = emotion === "happy" ? "Q 400,345 410,330" : emotion === "sad" ? "Q 400,325 410,340" : "L 410,335";
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Hair */}
      <ellipse cx="400" cy="160" rx="50" ry="55" fill="#7c3aed" />
      <path d="M350,170 Q340,250 360,280" fill="#7c3aed" />
      <path d="M450,170 Q460,250 440,280" fill="#7c3aed" />
      {/* Head */}
      <circle cx="400" cy="180" r="40" fill="#fbbf24">
        <animateTransform attributeName="transform" type="translate" values={`0,0;0,${breathe};0,0`} dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Eyes */}
      <ellipse cx="385" cy="175" rx="4" ry="5" fill="#1e293b">
        <animate attributeName="ry" values="5;0.5;5" dur="4s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="415" cy="175" rx="4" ry="5" fill="#1e293b">
        <animate attributeName="ry" values="5;0.5;5" dur="4s" repeatCount="indefinite" />
      </ellipse>
      {/* Mouth */}
      <path d={`M390,335 ${smileCurve}`.replace(/335|330|340|325/g, (m) => String(Number(m) - 145))} stroke="#1e293b" strokeWidth="2" fill="none" />
      {/* Blush */}
      <circle cx="370" cy="195" r="8" fill="#f472b6" opacity="0.4" />
      <circle cx="430" cy="195" r="8" fill="#f472b6" opacity="0.4" />
      {/* Body */}
      <path d={`M370,${220 + breathe} Q400,${210 + breathe} 430,${220 + breathe} L440,320 Q400,340 360,320 Z`} fill="#ec4899" />
      {/* Arms */}
      <path d={`M365,${240 + breathe} Q330,280 320,310`} stroke="#fbbf24" strokeWidth="10" strokeLinecap="round" fill="none">
        <animateTransform attributeName="transform" type="rotate" values="0,365,240;-5,365,240;0,365,240" dur="2s" repeatCount="indefinite" />
      </path>
      <path d={`M435,${240 + breathe} Q470,280 480,310`} stroke="#fbbf24" strokeWidth="10" strokeLinecap="round" fill="none">
        <animateTransform attributeName="transform" type="rotate" values="0,435,240;5,435,240;0,435,240" dur="2.5s" repeatCount="indefinite" />
      </path>
      {/* Skirt */}
      <path d="M360,320 Q340,400 330,420 L470,420 Q460,400 440,320 Z" fill="#a855f7" />
      {/* Legs */}
      <rect x="370" y="415" width="12" height="50" rx="5" fill="#fbbf24" />
      <rect x="418" y="415" width="12" height="50" rx="5" fill="#fbbf24" />
      {/* Shadow */}
      <ellipse cx="400" cy="475" rx="60" ry="10" fill="#1e293b" opacity="0.2" />
      <text x="400" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="16">{emotion === "happy" ? "😊" : emotion === "sad" ? "😢" : "🙂"}</text>
    </svg>
  );
};

// ── 4. Single Boy ────────────────────────────────────────────────────
export const SingleBoy: React.FC<Record<string, unknown>> = (props) => {
  const emotion = (props.emotion as string) ?? "neutral";
  const phase = (props.phase as number) ?? 0;
  const bounce = Math.abs(Math.sin(phase * 0.8)) * 3;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Hair */}
      <path d="M360,150 Q370,120 400,115 Q430,120 440,150 L440,165 Q420,155 380,155 Z" fill="#1e293b" />
      {/* Head */}
      <circle cx="400" cy="180" r="38" fill="#d4a574">
        <animateTransform attributeName="transform" type="translate" values={`0,0;0,${-bounce};0,0`} dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Eyes */}
      <circle cx="387" cy="175" r="4" fill="#1e293b" />
      <circle cx="413" cy="175" r="4" fill="#1e293b" />
      {/* Smile */}
      {emotion === "happy" && <path d="M388,192 Q400,204 412,192" stroke="#1e293b" strokeWidth="2" fill="none" />}
      {emotion === "neutral" && <line x1="390" y1="195" x2="410" y2="195" stroke="#1e293b" strokeWidth="2" />}
      {/* Body - T-shirt */}
      <path d={`M368,${220 - bounce} L432,${220 - bounce} L440,340 L360,340 Z`} fill="#3b82f6" />
      <text x="400" y={290 - bounce} textAnchor="middle" fill="#bfdbfe" fontSize="16" fontWeight="bold">★</text>
      {/* Arms */}
      <line x1="365" y1={230 - bounce} x2="320" y2={290 - bounce} stroke="#d4a574" strokeWidth="12" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" values="0,365,230;-8,365,230;0,365,230" dur="1.5s" repeatCount="indefinite" />
      </line>
      <line x1="435" y1={230 - bounce} x2="480" y2={290 - bounce} stroke="#d4a574" strokeWidth="12" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" values="0,435,230;8,435,230;0,435,230" dur="1.8s" repeatCount="indefinite" />
      </line>
      {/* Pants */}
      <rect x="365" y="335" width="30" height="60" rx="5" fill="#1e293b" />
      <rect x="405" y="335" width="30" height="60" rx="5" fill="#1e293b" />
      {/* Shoes */}
      <ellipse cx="380" cy="400" rx="18" ry="8" fill="#ef4444" />
      <ellipse cx="420" cy="400" rx="18" ry="8" fill="#ef4444" />
      {/* Shadow */}
      <ellipse cx="400" cy="415" rx="50" ry="8" fill="#1e293b" opacity="0.15" />
    </svg>
  );
};

// ── 5. Teacher & Student ─────────────────────────────────────────────
export const TeacherStudent: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Blackboard */}
      <rect x="180" y="40" width="440" height="200" rx="8" fill="#064e3b" stroke="#854d0e" strokeWidth="6" />
      <rect x="200" y="60" width="400" height="160" fill="#065f46" />
      {/* Chalk text */}
      <text x="400" y="120" textAnchor="middle" fill="#d1fae5" fontSize="24" fontFamily="serif">E = mc²</text>
      <line x1="280" y1="160" x2="520" y2="160" stroke="#d1fae5" strokeWidth="1.5" opacity="0.6" />
      <text x="400" y="195" textAnchor="middle" fill="#a7f3d0" fontSize="14">Energy = mass × speed of light²</text>
      {/* Teacher */}
      <g opacity={activeIndex >= 0 ? 1 : 0.4}>
        <circle cx="180" cy="310" r="25" fill="#f59e0b" />
        <rect x="167" y="335" width="26" height="45" rx="8" fill="#475569" />
        <rect x="163" y="380" width="14" height="40" rx="4" fill="#334155" />
        <rect x="181" y="380" width="14" height="40" rx="4" fill="#334155" />
        {/* Pointer */}
        <line x1="207" y1="340" x2="280" y2="200" stroke="#a16207" strokeWidth="3" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="0,207,340;-5,207,340;5,207,340;0,207,340" dur="3s" repeatCount="indefinite" />
        </line>
        <text x="180" y="445" textAnchor="middle" fill="#fbbf24" fontSize="12">Teacher</text>
      </g>
      {/* Students */}
      {[0, 1, 2].map((i) => (
        <g key={i} opacity={activeIndex >= i + 1 ? 1 : 0.4}>
          <circle cx={400 + i * 100} cy={370} r="18" fill={["#6366f1", "#ec4899", "#10b981"][i]} />
          <rect x={390 + i * 100} y="388" width="20" height="30" rx="6" fill={["#4f46e5", "#db2777", "#059669"][i]} />
          {/* Raised hand for active student */}
          {activeIndex === i + 1 && (
            <line x1={405 + i * 100} y1="388" x2={415 + i * 100} y2="340" stroke={["#6366f1", "#ec4899", "#10b981"][i]} strokeWidth="6" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" values={`0,${405 + i * 100},388;-10,${405 + i * 100},388;0,${405 + i * 100},388`} dur="1s" repeatCount="indefinite" />
            </line>
          )}
          <text x={400 + i * 100} y="445" textAnchor="middle" fill="#94a3b8" fontSize="11">Student {i + 1}</text>
        </g>
      ))}
    </svg>
  );
};

// ── 6. Handshake ─────────────────────────────────────────────────────
export const HandshakeDeal: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const shakeOffset = Math.sin(animationProgress * Math.PI * 6) * 4;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <defs>
        <linearGradient id="hs-glow" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fbbf24" stopOpacity="0.3" /><stop offset="100%" stopColor="#fbbf24" stopOpacity="0" /></linearGradient>
      </defs>
      {/* Glow behind */}
      <circle cx="400" cy="250" r={80 + animationProgress * 40} fill="url(#hs-glow)" opacity={0.3 + animationProgress * 0.4}>
        <animate attributeName="r" values="80;120;80" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Left arm */}
      <path d={`M100,280 Q200,260 320,${250 + shakeOffset}`} stroke="#d4a574" strokeWidth="28" strokeLinecap="round" fill="none" />
      {/* Right arm */}
      <path d={`M700,280 Q600,260 480,${250 + shakeOffset}`} stroke="#8b6914" strokeWidth="28" strokeLinecap="round" fill="none" />
      {/* Clasped hands */}
      <g transform={`translate(0,${shakeOffset})`}>
        <ellipse cx="400" cy="250" rx="55" ry="35" fill="#d4a574" />
        <ellipse cx="400" cy="250" rx="50" ry="30" fill="#c6975c" />
        <path d="M360,245 Q380,230 400,245 Q420,230 440,245" stroke="#a67940" strokeWidth="2" fill="none" />
      </g>
      {/* Sparkles */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i / 5) * Math.PI * 2;
        const r = 100 + animationProgress * 30;
        return (
          <circle key={i} cx={400 + Math.cos(angle) * r} cy={250 + Math.sin(angle) * r} r="3" fill="#fbbf24">
            <animate attributeName="opacity" values="0;1;0" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="r" values="2;5;2" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      <text x="400" y="400" textAnchor="middle" fill="#fbbf24" fontSize="18" fontWeight="bold">Agreement</text>
    </svg>
  );
};

// ── 7. Crowd Scene ───────────────────────────────────────────────────
export const CrowdScene: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const colors = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6", "#f97316", "#06b6d4", "#84cc16", "#e11d48"];
  const rows = [
    { y: 350, count: 7, r: 14, startX: 150 },
    { y: 310, count: 6, r: 13, startX: 180 },
    { y: 275, count: 5, r: 12, startX: 220 },
    { y: 245, count: 4, r: 11, startX: 260 },
  ];
  let colorIdx = 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {rows.map((row, ri) => (
        <g key={ri}>
          {Array.from({ length: row.count }).map((_, ci) => {
            const cx = row.startX + ci * ((800 - row.startX * 2) / (row.count - 1 || 1));
            const c = colors[colorIdx++ % colors.length];
            const bounce = Math.sin((animationProgress * 10 + colorIdx) * 0.7) * 3;
            return (
              <g key={ci}>
                <circle cx={cx} cy={row.y + bounce} r={row.r} fill={c} opacity="0.85" />
                <circle cx={cx} cy={row.y - row.r + 2 + bounce} r="2" fill="#fff" opacity="0.6" />
              </g>
            );
          })}
        </g>
      ))}
      {/* Arms waving */}
      {[200, 350, 500, 650].map((x, i) => (
        <line key={i} x1={x} y1="340" x2={x + 10} y2="310" stroke={colors[i * 3 % colors.length]} strokeWidth="4" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values={`0,${x},340;-20,${x},340;0,${x},340`} dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
        </line>
      ))}
      <text x="400" y="450" textAnchor="middle" fill="#e0e7ff" fontSize="18" fontWeight="bold">Community</text>
    </svg>
  );
};

// ── 8. Couple / Love ─────────────────────────────────────────────────
export const CoupleLove: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const heartBeat = 1 + Math.sin(phase * 2) * 0.08;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sunset gradient background */}
      <defs>
        <linearGradient id="cp-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e1b4b" /><stop offset="50%" stopColor="#4c1d95" /><stop offset="100%" stopColor="#f59e0b" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="800" height="400" fill="url(#cp-sky)" rx="12" />
      {/* Stars */}
      {[60, 150, 300, 550, 700, 120, 650].map((x, i) => (
        <circle key={i} cx={x} cy={30 + i * 15} r="1.5" fill="#fff">
          <animate attributeName="opacity" values="0.3;1;0.3" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Ground */}
      <ellipse cx="400" cy="420" rx="380" ry="30" fill="#065f46" />
      {/* Person 1 */}
      <g>
        <circle cx="330" cy="290" r="22" fill="#fbbf24" />
        <rect x="318" y="312" width="24" height="40" rx="7" fill="#6366f1" />
        <rect x="316" y="352" width="10" height="35" rx="4" fill="#4f46e5" />
        <rect x="334" y="352" width="10" height="35" rx="4" fill="#4f46e5" />
        <animateTransform attributeName="transform" type="translate" values="0,0;2,-1;0,0" dur="3s" repeatCount="indefinite" />
      </g>
      {/* Person 2 */}
      <g>
        <circle cx="470" cy="290" r="22" fill="#fbbf24" />
        <path d="M450,312 Q460,365 470,370 Q480,365 490,312 Z" fill="#ec4899" />
        <rect x="456" y="370" width="10" height="30" rx="4" fill="#db2777" />
        <rect x="474" y="370" width="10" height="30" rx="4" fill="#db2777" />
        <animateTransform attributeName="transform" type="translate" values="0,0;-2,-1;0,0" dur="3s" repeatCount="indefinite" />
      </g>
      {/* Heart between them */}
      <g transform={`translate(400,230) scale(${heartBeat})`}>
        <path d="M0,-15 C-15,-35 -40,-10 0,20 C40,-10 15,-35 0,-15 Z" fill="#ef4444">
          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.2s" repeatCount="indefinite" />
        </path>
      </g>
      {/* Floating hearts */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <path d={`M${350 + i * 50},${200 - i * 30} c-5,-8 -15,-3 0,8 c15,-11 5,-16 0,-8`} fill="#f472b6" opacity="0.5">
            <animateTransform attributeName="transform" type="translate" values={`0,0;${i * 5 - 5},-30;${i * 3},0`} dur={`${3 + i}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0;0.5" dur={`${3 + i}s`} repeatCount="indefinite" />
          </path>
        </g>
      ))}
      <text x="400" y="470" textAnchor="middle" fill="#f9a8d4" fontSize="16" fontWeight="bold">Love & Connection</text>
    </svg>
  );
};
